import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppPushParamFieldChange,
    applyAppPushDraft,
    applyAppPushTargetSelection,
    clearAppPushDraft,
    createAppPushSubmitFormData,
    createDefaultAppPushParams,
    createAppPushDraftPayload,
    createEditAppPushDraftKey,
    fetchAppPushListData,
    getAppPushCoupangSearchState,
    getAppPushSubmitErrorMessage,
    readAppPushDraft,
    saveAppPushDraft,
    toggleAppPushWeekType,
} from './appPushDraftUtils'
import AppPushFormFields from './AppPushFormFields'
import { AppPushCoupangTargetTable, AppPushMeetTargetTable } from './AppPushTargetTables'
import {
    getLegacyTimeTypeFromReserveTime,
    getReserveTimeFromDetail,
} from './pushReserveTime'

export default function AppPush(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.pushHistoryId
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || { searchStatus: 99, searchPopupStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditAppPushDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [meetWeTotalCount, setMeetWeTotalCount] = useState(0)
    const [coupangTotalCount, setCoupangTotalCount] = useState(0)
    const [curPage, setCurPage] = useState(1)
    const [meetWeCurPage, setMeetWeCurPage] = useState(1)
    const [coupangCurPage, setCoupangCurPage] = useState(1)
    const offset = 5
    const [animalDatas, setAnimalDatas] = useState([])
    const [meetWeDatas, setMeetWeDatas] = useState([])
    const [coupangDatas, setCoupangDatas] = useState([])
    const [params, setParams] = useState(() => createDefaultAppPushParams())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [searchText, setSearchText] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchSelect] = useState({
        keyword1: locationState.searchSelect?.keyword1 || '',
    })
    const [checked, setChecked] = useState([])
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 App 푸시 상세를 불러올 수 없습니다.'
                setParams(createDefaultAppPushParams())
                setChecked([])
                setHasDetail(false)
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appPushHistory/getDetail', { pushHistoryId: id })
                // console.log('getDetail: ', data )
                if (data.code === 200) {
                    const baseData = data.data || {}

                    if (Object.keys(baseData).length === 0) {
                        setParams(createDefaultAppPushParams())
                        setChecked([])
                        setDetailErrorMessage('App 푸시 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const reserveTime = getReserveTimeFromDetail(baseData)
                    const baseParams = {
                        ...baseData,
                        reserveHour: reserveTime.reserveHour,
                        reserveMinute: reserveTime.reserveMinute,
                    }
                    const baseChecked = baseData.weekType ? baseData.weekType.split(',').map(Number).filter((value) => !Number.isNaN(value)).sort((a, b) => a - b) : []
                    const storedDraft = readAppPushDraft(draftKey)
                    const restoredDraft = applyAppPushDraft({
                        baseParams,
                        baseChecked,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppPushDraftPayload({
                        params: baseParams,
                        checked: baseChecked,
                    }))

                    setParams(restoredDraft.params)
                    setChecked(restoredDraft.checked)
                    setCurPage(restoredDraft.curPage)
                    setMeetWeCurPage(restoredDraft.meetWeCurPage)
                    setCoupangCurPage(restoredDraft.coupangCurPage)
                    setSearchText(restoredDraft.searchText)
                    setSearchQuery(restoredDraft.searchQuery)
                    setHasDetail(true)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'App 푸시 상세를 불러오지 못했습니다.'
                    setParams(createDefaultAppPushParams())
                    setChecked([])
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'App 푸시 상세를 불러오지 못했습니다.'
                setParams(createDefaultAppPushParams())
                setChecked([])
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }

            setLoading(false)
        }
        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // App 푸시 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppPushDraftPayload({
            params,
            checked,
            curPage,
            meetWeCurPage,
            coupangCurPage,
            searchText,
            searchQuery,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppPushDraft(draftKey)
            return
        }

        saveAppPushDraft(draftKey, nextDraft)
    }, [checked, coupangCurPage, curPage, draftKey, draftReady, hasDetail, meetWeCurPage, params, searchQuery, searchText])

    const showListLoadError = useCallback((message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar('error')
        setMessageSnackbar(message)
    }, [])
    
    const getAnimalData = useCallback(async () => {
        await fetchAppPushListData({
            request: (payload) => API.post('/api/v1/admin/appPushHistory/getAnimalList', payload),
            payload: { page: curPage, offset },
            setTotalCount,
            setRows: setAnimalDatas,
            onForbidden: () => history.push('/login'),
            onError: showListLoadError,
            fallbackErrorMessage: '애니멀런 목록을 불러오지 못했습니다.',
        })
    }, [curPage, history, offset, showListLoadError])

    const getMeetWeData = useCallback(async () => {
        await fetchAppPushListData({
            request: (payload) => API.post('/api/v1/admin/appPushHistory/getMeetWeList', payload),
            payload: { page: meetWeCurPage, offset },
            setTotalCount: setMeetWeTotalCount,
            setRows: setMeetWeDatas,
            onForbidden: () => history.push('/login'),
            onError: showListLoadError,
            fallbackErrorMessage: '우리끼리 대회 목록을 불러오지 못했습니다.',
        })
    }, [history, meetWeCurPage, offset, showListLoadError])

    const getCoupangData = useCallback(async () => {
        await fetchAppPushListData({
            request: (payload) => API.post('/api/v1/admin/appPushHistory/getCoupangList', payload),
            payload: {
                page: coupangCurPage,
                offset,
                searchText: searchQuery,
                searchSelect,
            },
            setTotalCount: setCoupangTotalCount,
            setRows: setCoupangDatas,
            onForbidden: () => history.push('/login'),
            onError: showListLoadError,
            fallbackErrorMessage: '쿠팡 상품 목록을 불러오지 못했습니다.',
        })
    }, [coupangCurPage, history, offset, searchQuery, searchSelect, showListLoadError])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    useEffect(() => {
        if (Number(params.pushType) !== 2) return
        getAnimalData()
    }, [getAnimalData, params.pushType])
    useEffect(() => {
        if (Number(params.pushType) !== 4) return
        getMeetWeData()
    }, [getMeetWeData, params.pushType])
    useEffect(() => {
        if (Number(params.pushType) !== 3) return
        getCoupangData()
    }, [getCoupangData, params.pushType])

    const handleUpdateField = (e) => {
        if (e.target.name === 'pushType') {
            setCurPage(1)
            setMeetWeCurPage(1)
            setCoupangCurPage(1)
        }

        setParams(applyAppPushParamFieldChange({
            params,
            name: e.target.name,
            value: e.target.value,
        }))
    }

    const handleReserveTimeChange = (name) => (e) => {
        setParams({
            ...params,
            [name]: e.target.value,
        })
    }

    const updateData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appPushHistory/update',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })
            
            if (data.code === 200) {
                // 저장 후에도 운영자가 보던 App 푸시 목록 조건으로 그대로 복귀시킨다.
                clearAppPushDraft(draftKey)
                history.push(createListLinkTarget({
                    pathname: `/appPushs`,
                    search: listSearch,
                    openSnackbar: true,
                    severitySnackbar: 'success',
                    messageSnackbar: data.message,
                    curPage: listCurPage,
                    searchText: listSearchText,
                    searchSelect: listSearchSelect
                }))
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 푸시를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const submitErrorMessage = getAppPushSubmitErrorMessage({
            params,
            checked,
        })

        if (submitErrorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(submitErrorMessage)
            return false
        }

        const formData = createAppPushSubmitFormData({
            params,
            checked,
            pushHistoryId: id,
            getLegacyTimeTypeFromReserveTime,
        })

        updateData(formData)
    }

    const chooseButtonClick = (meetId) => {
        setParams(applyAppPushTargetSelection({
            params,
            field: 'meetId',
            value: meetId,
        }))
    }

    const chooseButtonClick2 = (coupangId) => {
        setParams(applyAppPushTargetSelection({
            params,
            field: 'coupangId',
            value: coupangId,
        }))
    }

    const handleChange = (e) => {
        setChecked(toggleAppPushWeekType({
            checked,
            value: e.target.value,
            isChecked: e.target.checked,
        }))
    }

    const searchButtonClick = () => {
        const { shouldFetchCurrentPage, shouldResetPage, nextSearchQuery } = getAppPushCoupangSearchState({
            currentPage: coupangCurPage,
            searchQuery,
            searchText,
        })

        if (shouldFetchCurrentPage) {
            getCoupangData()
            return
        }

        setSearchQuery(nextSearchQuery)

        if (shouldResetPage) {
            setCoupangCurPage(1)
        }
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 푸시 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appPushs" search={listSearch} visible={false} curPage={listCurPage} searchText={listSearchText} searchSelect={listSearchSelect} onBackClick={() => clearAppPushDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>APP 푸시 발송 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>APP 푸시 발송 수정</Title>
                {/* 애니멀런 상품 리스트 */}
                {params.pushType === 2 &&
                <AppPushMeetTargetTable
                    rows={animalDatas}
                    totalCount={totalCount}
                    curPage={curPage}
                    offset={offset}
                    setCurPage={setCurPage}
                    selectedMeetId={params.meetId}
                    imageHeader="상품이미지"
                    titleHeader="상품명"
                    rowKey={(row) => row.meetAnimalId}
                    imageSrc={(row) => `${process.env.REACT_APP_AWS_S3_URL}/${row.thumbnail}`}
                    onSelect={(row) => chooseButtonClick(row.meetId)}
                    saving={saving}
                />
                }
                {/* 우리끼리 대회 리스트 */}
                {params.pushType === 4 &&
                <AppPushMeetTargetTable
                    rows={meetWeDatas}
                    totalCount={meetWeTotalCount}
                    curPage={meetWeCurPage}
                    offset={offset}
                    setCurPage={setMeetWeCurPage}
                    selectedMeetId={params.meetId}
                    imageHeader="이미지"
                    titleHeader="대회명"
                    rowKey={(row) => row.meetId}
                    imageSrc={(row) => `${process.env.REACT_APP_AWS_S3_URL}/${row.thumbnail}`}
                    onSelect={(row) => chooseButtonClick(row.meetId)}
                    saving={saving}
                />
                }
                {/* 쿠팡 상품 리스트 */}
                { params.pushType === 3 &&
                <AppPushCoupangTargetTable
                    rows={coupangDatas}
                    captionTotalCount={totalCount}
                    totalCount={coupangTotalCount}
                    curPage={coupangCurPage}
                    offset={offset}
                    setCurPage={setCoupangCurPage}
                    selectedCoupangId={params.coupangId}
                    searchText={searchText}
                    onSearchTextChange={(e) => setSearchText(e.target.value)}
                    onSearchSubmit={(e) => {
                        e.preventDefault()
                        searchButtonClick()
                    }}
                    onSelect={(row) => chooseButtonClick2(row.coupangId)}
                    saving={saving}
                />
                }
                <AppPushFormFields
                    params={params}
                    checked={checked}
                    saving={saving}
                    onSubmit={handleSubmit}
                    onFieldChange={handleUpdateField}
                    onReserveHourChange={handleReserveTimeChange('reserveHour')}
                    onReserveMinuteChange={handleReserveTimeChange('reserveMinute')}
                    onWeekTypeChange={handleChange}
                    onStartTimeChange={(date) => setParams({ ...params, startTime: date })}
                    onEndTimeChange={(date) => setParams({ ...params, endTime: date })}
                    listSearch={listSearch}
                    listCurPage={listCurPage}
                    listSearchText={listSearchText}
                    listSearchSelect={listSearchSelect}
                    onBackClick={() => clearAppPushDraft(draftKey)}
                    submitLabel={saving ? '저장 중...' : '저장'}
                />
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
